import type {
  Badge,
  ConnectionWithSharing,
  MedicationReminder,
  PersonalMedicalData,
  InsuranceInfo,
  Study,
  UserPreferences,
  YouScreenState,
  Language,
} from "./types";
import { getDeviceTimezone } from "@src/packages/utils/date";

// ── Mock Data ──

const INITIAL_BADGES: Badge[] = [
  // ── Getting Started ──
  {
    id: "first-steps",
    name: "First Steps",
    hint: "Complete your About You medical history",
    description: "You shared the first details that help shape your care.",
    category: "getting-started",
    unlocked: false,
  },
  {
    id: "profile-complete",
    name: "Profile Complete",
    hint: "Fill out your full profile",
    description:
      "Your profile is complete. Your care team has the full picture.",
    category: "getting-started",
    unlocked: false,
  },
  {
    id: "photo-day",
    name: "Photo Day",
    hint: "Upload a profile picture",
    description: "You added your photo. Now your profile feels like you.",
    category: "getting-started",
    unlocked: false,
  },
  {
    id: "insurance-navigator",
    name: "Insurance Navigator",
    hint: "Add your insurance information",
    description:
      "Insurance added: one less thing to worry about at the clinic.",
    category: "getting-started",
    unlocked: false,
  },
  {
    id: "clinic-finder",
    name: "Clinic Finder",
    hint: "Connect with a fertility clinic",
    description: "You found your clinic. A team is forming around you.",
    category: "getting-started",
    unlocked: false,
  },
  {
    id: "treatment-decided",
    name: "Treatment Decided",
    hint: "Choose your treatment path",
    description: "You chose your path forward. Clarity is powerful.",
    category: "getting-started",
    unlocked: false,
  },
  {
    id: "phone-verified",
    name: "Phone Verified",
    hint: "Verify your phone number",
    description: "Phone verified. We can reach you when it matters.",
    category: "getting-started",
    unlocked: false,
  },

  // ── Clinical Milestones ──
  {
    id: "lab-explorer",
    name: "Lab Explorer",
    hint: "Upload your first test result",
    description:
      "You uploaded your first lab results. Knowledge is power on this journey.",
    category: "clinical",
    unlocked: false,
  },
  {
    id: "appointment-booked",
    name: "Appointment Set",
    hint: "Book your first care appointment",
    description:
      "Your first appointment is on the calendar: one clear step forward.",
    category: "clinical",
    unlocked: false,
  },
  {
    id: "clinic-day",
    name: "Clinic Day",
    hint: "Attend your first clinic visit",
    description:
      "You showed up for your first clinic appointment. That takes courage.",
    category: "clinical",
    unlocked: false,
  },
  {
    id: "protocol-pioneer",
    name: "Protocol Pioneer",
    hint: "Start your treatment protocol",
    description: "Protocol started. You're officially in motion.",
    category: "clinical",
    unlocked: false,
  },
  {
    id: "first-retrieval",
    name: "First Retrieval",
    hint: "Complete your first egg retrieval",
    description: "Retrieval complete. You did something extraordinary today.",
    category: "clinical",
    unlocked: false,
  },
  {
    id: "embryo-update",
    name: "Embryo Update",
    hint: "Receive your first embryo development report",
    description: "Your first embryo report is in. Every update is a milestone.",
    category: "clinical",
    unlocked: false,
  },
  {
    id: "transfer-day",
    name: "Transfer Day",
    hint: "Complete an embryo transfer",
    description: "Transfer done. Now begins the most hopeful kind of waiting.",
    category: "clinical",
    unlocked: false,
  },
  {
    id: "the-wait",
    name: "The Wait",
    hint: "Reach the two-week wait",
    description: "The two-week wait is real. You're handling it with grace.",
    category: "clinical",
    unlocked: false,
  },

  // ── Medication & Self-Care ──
  {
    id: "consistent",
    name: "Consistent",
    hint: "7-day supplement streak",
    description:
      "Seven days straight of taking your supplements. Consistency builds results.",
    category: "self-care",
    unlocked: false,
  },
  {
    id: "supplement-starter",
    name: "Supplement Starter",
    hint: "Log your first supplement",
    description: "First supplement logged. Small habits make big differences.",
    category: "self-care",
    unlocked: false,
  },
  {
    id: "medication-streak-3",
    name: "Three-Day Rhythm",
    hint: "Log medications for 3 days",
    description:
      "Three days of medication tracking. You’re finding your rhythm.",
    category: "self-care",
    unlocked: false,
  },
  {
    id: "medication-streak-7",
    name: "Steady Routine",
    hint: "Log medications for 7 days",
    description: "A full week of tracking. Small, steady steps add up.",
    category: "self-care",
    unlocked: false,
  },
  {
    id: "med-tracker",
    name: "Medication Tracker",
    hint: "Log medications for 14 days",
    description:
      "Two weeks of tracking. Your dedication is building a clear picture.",
    category: "self-care",
    unlocked: false,
  },
  {
    id: "hydration-hero",
    name: "Hydration Hero",
    hint: "Log water intake for 7 days",
    description: "A week of staying hydrated. Your body thanks you.",
    category: "self-care",
    unlocked: false,
  },
  {
    id: "hydration-streak-3",
    name: "Hydration Start",
    hint: "Log water intake for 3 days",
    description: "Three days of hydration tracking: a caring habit in motion.",
    category: "self-care",
    unlocked: false,
  },
  {
    id: "mindful-moment",
    name: "Mindful Moment",
    hint: "Complete a guided relaxation",
    description: "You made time for calm. Your mind and body are connected.",
    category: "self-care",
    unlocked: false,
  },

  // ── Knowledge & Learning ──
  {
    id: "first-article",
    name: "First Read",
    hint: "Read your first article",
    description:
      "You started building your knowledge with one thoughtful read.",
    category: "knowledge",
    unlocked: false,
  },
  {
    id: "knowledge-seeker",
    name: "Knowledge Seeker",
    hint: "Read 5 articles",
    description:
      "Five articles read. You're building real understanding of your fertility.",
    category: "knowledge",
    unlocked: false,
  },
  {
    id: "deep-diver",
    name: "Deep Diver",
    hint: "Read 20 articles",
    description:
      "Twenty articles deep. You're becoming your own best advocate.",
    category: "knowledge",
    unlocked: false,
  },
  {
    id: "myth-buster",
    name: "Myth Buster",
    hint: "Read a fertility myths article",
    description: "Another myth busted. Real knowledge replaces fear.",
    category: "knowledge",
    unlocked: false,
  },
  {
    id: "question-master",
    name: "Question Master",
    hint: "Ask 3 questions to your care team",
    description: "Three questions asked. The best patients are curious ones.",
    category: "knowledge",
    unlocked: false,
  },
  {
    id: "resource-collector",
    name: "Resource Collector",
    hint: "Save 5 resources to your library",
    description:
      "Five resources saved. You're building a personal knowledge base.",
    category: "knowledge",
    unlocked: false,
  },
  {
    id: "first-resource",
    name: "Resource Keeper",
    hint: "Save your first resource to your library",
    description: "You saved a resource to return to when you need it.",
    category: "knowledge",
    unlocked: false,
  },
  {
    id: "informed-consent",
    name: "Informed Consent",
    hint: "Review your treatment consent forms",
    description:
      "Consent reviewed thoughtfully. Informed decisions are empowered decisions.",
    category: "knowledge",
    unlocked: false,
  },
  {
    id: "research-reader",
    name: "Research Reader",
    hint: "Read a clinical study summary",
    description:
      "You went straight to the research. Evidence-based is the way.",
    category: "knowledge",
    unlocked: false,
  },

  // ── Community & Connection ──
  {
    id: "connector",
    name: "Connector",
    hint: "Connect with your partner",
    description:
      "Your partner is connected to your journey. Together is stronger.",
    category: "community",
    unlocked: false,
  },
  {
    id: "data-owner",
    name: "Data Owner",
    hint: "Export your data",
    description:
      "You exported your data. Your health information belongs to you, always.",
    category: "community",
    unlocked: false,
  },
  {
    id: "story-sharer",
    name: "Story Sharer",
    hint: "Share your journey update",
    description: "You shared your story. Vulnerability inspires others.",
    category: "community",
    unlocked: false,
  },
  {
    id: "support-circle",
    name: "Support Circle",
    hint: "Connect with 3 community members",
    description: "Three connections made. Your support circle is growing.",
    category: "community",
    unlocked: false,
  },
  {
    id: "buddy-system",
    name: "Buddy System",
    hint: "Send a message to your partner",
    description:
      "You reached out to your partner. No one should do this alone.",
    category: "community",
    unlocked: false,
  },
  {
    id: "feedback-champion",
    name: "Feedback Champion",
    hint: "Submit feedback about the app",
    description:
      "Your feedback helps us build better. Thank you for speaking up.",
    category: "community",
    unlocked: false,
  },
  {
    id: "ambassador",
    name: "Ambassador",
    hint: "Invite a friend to Louise",
    description:
      "You invited a friend. Word of mouth is the highest compliment.",
    category: "community",
    unlocked: false,
  },

  // ── Journey Milestones ──
  {
    id: "one-month-in",
    name: "One Month In",
    hint: "Use Louise for 30 days",
    description: "One month on your journey. You're showing up for yourself.",
    category: "milestone",
    unlocked: false,
  },
  {
    id: "quarter-mark",
    name: "Quarter Mark",
    hint: "Use Louise for 90 days",
    description: "Three months in. Your commitment to yourself is inspiring.",
    category: "milestone",
    unlocked: false,
  },
  {
    id: "halfway-there",
    name: "Halfway There",
    hint: "Use Louise for 6 months",
    description: "Six months strong. You've built real momentum.",
    category: "milestone",
    unlocked: false,
  },
  {
    id: "full-circle",
    name: "Full Circle",
    hint: "Use Louise for one year",
    description: "A full year. Look how far you've come.",
    category: "milestone",
    unlocked: false,
  },
];

const DEFAULT_PERMISSIONS = {
  medicalHistory: false,
  testResults: false,
  medications: false,
  cycleData: false,
  appointments: false,
  documents: false,
};

const INITIAL_CONNECTIONS: ConnectionWithSharing[] = [
  {
    type: "partner",
    name: null,
    status: "not_connected",
    permissions: { ...DEFAULT_PERMISSIONS },
  },
  {
    type: "clinic",
    name: null,
    status: "not_connected",
    permissions: { ...DEFAULT_PERMISSIONS },
  },
  {
    type: "pharmacy",
    name: null,
    status: "not_connected",
    permissions: { ...DEFAULT_PERMISSIONS },
  },
  {
    type: "provider",
    name: null,
    status: "not_connected",
    permissions: { ...DEFAULT_PERMISSIONS },
  },
];

// Empty by default — users add their own medication reminders.
const INITIAL_MED_REMINDERS: MedicationReminder[] = [];

const INITIAL_PERSONAL_MEDICAL: PersonalMedicalData = {
  firstName: null,
  lastName: null,
  email: null,
  phone: null,
  dateOfBirth: null,
  gender: null,
  emergencyContact: null,
  bloodType: null,
  allergies: [],
  hasAllergies: null,
  address: null,
};

const INITIAL_INSURANCE: InsuranceInfo = {
  provider: null,
  planName: null,
  memberId: null,
};

// Eligibility-driven studies will populate from the matching API. Until then,
// an empty list accurately means that there are no eligible studies to show.
const INITIAL_STUDIES: Study[] = [];

// ── Initial State ──

const INITIAL_PREFERENCES: UserPreferences = {
  language: "en",
  dateFormat: "MM/DD/YYYY",
  timeFormat: "12H",
  timezone: getDeviceTimezone(),
  timezoneLocation: null,
  hapticsEnabled: true,
  soundEnabled: false,
};

export const LANGUAGE_LABELS: Record<Language, string> = {
  en: "English",
  fr: "Français",
};

export function getBadgeDefinition(id: string): Badge | null {
  return INITIAL_BADGES.find((badge) => badge.id === id) ?? null;
}

export function getInitialState(): YouScreenState {
  return {
    badges: INITIAL_BADGES,
    connections: INITIAL_CONNECTIONS,
    medicationReminders: INITIAL_MED_REMINDERS,
    personalMedical: INITIAL_PERSONAL_MEDICAL,
    insuranceInfo: INITIAL_INSURANCE,
    studies: INITIAL_STUDIES,
    preferences: INITIAL_PREFERENCES,
    selectedAnswer: null,
    activePanel: null,
    activeModal: null,
    dataSharingModal: null,
    intentPickerGenderOverride: null,
    deleteConfirmText: "",
    partnerInvitePhone: "",
    partnerInviteName: "",
  };
}
